import { useEffect, useRef, useState } from 'react';
import { Box, Checkbox, FormControl, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Controller, useFormContext, useFormState, useWatch } from 'react-hook-form';

import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';

import { LabelComponent } from '../uiComponents/LabelComponent';
import { MyButton } from '../uiComponents/MyButton';
import styles from './PropertyAdEditor.module.scss';
import { PROPERTY_AD_VIEW_OPTIONS } from './propertyAdSpecificationOptions';

const VIEW_MENU_ITEM_HEIGHT = 48;
const VIEW_MENU_PADDING_TOP = 8;

const viewMenuProps = {
  PaperProps: {
    'data-testid': 'property-ad-view-menu',
    style: {
      maxHeight: VIEW_MENU_ITEM_HEIGHT * 10 + VIEW_MENU_PADDING_TOP,
      width: 250,
    },
  },
} as const;

const isStandardView = (value: string): boolean =>
  PROPERTY_AD_VIEW_OPTIONS.some((option) => option === value);

const getFirstCustomView = (values: string[]): { index: number; value: string } | null => {
  const index = values.findIndex((value) => !isStandardView(value));

  return index === -1 ? null : { index, value: values[index] };
};

export const PropertyAdViewField = () => {
  const { control } = useFormContext<PropertyAdEditorFormValues>();
  const selectedViews = useWatch({ control, name: 'view' });
  const { isDirty } = useFormState({ control });
  const initialCustomView = getFirstCustomView(selectedViews);
  const [isCustomViewVisible, setIsCustomViewVisible] = useState(Boolean(initialCustomView));
  const [customViewIndex, setCustomViewIndex] = useState<number | null>(
    initialCustomView?.index ?? null
  );
  const [customViewDraft, setCustomViewDraft] = useState(initialCustomView?.value ?? '');
  const customViewDraftRef = useRef(initialCustomView?.value ?? '');
  const availableViewOptions = Array.from(new Set([...PROPERTY_AD_VIEW_OPTIONS, ...selectedViews]));

  useEffect(() => {
    if (isDirty) return;

    const hydratedCustomView = getFirstCustomView(selectedViews);
    customViewDraftRef.current = hydratedCustomView?.value ?? '';
    setIsCustomViewVisible(Boolean(hydratedCustomView));
    setCustomViewIndex(hydratedCustomView?.index ?? null);
    setCustomViewDraft(hydratedCustomView?.value ?? '');
  }, [isDirty, selectedViews]);

  return (
    <Box minWidth={0} sx={{ gridColumn: '1 / -1' }}>
      <LabelComponent label='View' />
      <Controller
        control={control}
        name='view'
        render={({ field }) => {
          const commitCustomView = () => {
            const draftToCommit = customViewDraftRef.current;

            if (!draftToCommit) return;

            customViewDraftRef.current = '';

            const nextViews = [...field.value];

            if (customViewIndex === null || customViewIndex >= nextViews.length) {
              nextViews.push(draftToCommit);
            } else {
              nextViews[customViewIndex] = draftToCommit;
            }

            field.onChange(nextViews);
            setCustomViewDraft('');
            setCustomViewIndex(null);
            setIsCustomViewVisible(false);
          };

          return (
            <Box
              sx={{
                display: 'grid',
                gap: { xs: 1.5, sm: 2.5 },
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                minWidth: 0,
                width: '100%',
              }}
            >
              <FormControl fullWidth>
                <Select<string[]>
                  className={styles.selectControl}
                  displayEmpty
                  inputProps={{ 'aria-label': 'View' }}
                  MenuProps={viewMenuProps}
                  multiple
                  value={field.value}
                  renderValue={(selected) => (
                    <Typography
                      className={`${styles.specificationSelectedValues} ${
                        selected.length > 0 ? styles.selectValue : styles.selectPlaceholder
                      }`}
                      component='span'
                      variant='inherit'
                    >
                      {selected.length > 0 ? selected.join(', ') : 'Select view'}
                    </Typography>
                  )}
                  onChange={(event) => {
                    const value = event.target.value;
                    const nextViews = typeof value === 'string' ? value.split(',') : value;
                    const selectedCustomView =
                      customViewIndex === null ? null : field.value[customViewIndex];
                    const nextCustomViewIndex = selectedCustomView
                      ? nextViews.indexOf(selectedCustomView)
                      : -1;

                    if (customViewIndex !== null && nextCustomViewIndex === -1) {
                      customViewDraftRef.current = '';
                      setCustomViewIndex(null);
                      setCustomViewDraft('');
                    } else if (nextCustomViewIndex !== -1) {
                      setCustomViewIndex(nextCustomViewIndex);
                    }

                    field.onChange(nextViews);
                  }}
                >
                  {availableViewOptions.map((value) => (
                    <MenuItem key={value} value={value}>
                      <Checkbox checked={field.value.includes(value)} />
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isCustomViewVisible ? (
                <TextField
                  fullWidth
                  required
                  className={styles.customViewInput}
                  inputProps={{ 'aria-label': 'Custom view', maxLength: 50 }}
                  placeholder='Enter custom view'
                  value={customViewDraft}
                  onBlur={commitCustomView}
                  onChange={(event) => {
                    customViewDraftRef.current = event.target.value;
                    setCustomViewDraft(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;

                    event.preventDefault();
                    commitCustomView();
                  }}
                />
              ) : (
                <Box minWidth={0}>
                  <MyButton
                    data={{
                      buttonName: 'Add custom view',
                      buttonType: 'button',
                      customWidth: '190px',
                      variant: 'contained',
                    }}
                    onClick={() => setIsCustomViewVisible(true)}
                  />
                </Box>
              )}
            </Box>
          );
        }}
      />
    </Box>
  );
};
